import React, { createContext, useContext } from "react";
import { cn } from "./cn";

/**
 * Semantic Bun table for settings and data grids. The outer frame scrolls
 * horizontally while the table retains native table structure and roles.
 */
const MobileCardsContext = createContext(false);

export default function DataTable({ embedded = false, mobileCards = false, className, tableClassName, children, ...props }) {
  return (
    <MobileCardsContext.Provider value={mobileCards}>
    <div
      className={cn(
        "w-full bg-bun-surface",
        mobileCards ? 'min-w-0 lg:overflow-x-auto' : 'overflow-x-auto',
        !embedded && "rounded-bun-lg border border-bun-line",
        className
      )}
    >
      <table
        className={cn(
          "w-full min-w-full border-collapse font-instrument text-bun-body font-book text-bun-text",
          mobileCards && 'block lg:table',
          tableClassName
        )}
        {...props}
      >
        {children}
      </table>
    </div>
    </MobileCardsContext.Provider>
  );
}

function Caption({ className, children, ...props }) {
  return (
    <caption
      className={cn("px-4 py-3 text-left text-[14px] font-semibold text-bun-text", className)}
      {...props}
    >
      {children}
    </caption>
  );
}

function Head({ className, children, ...props }) {
  const mobileCards = useContext(MobileCardsContext);
  return (
    <thead
      className={cn(
        "border-b border-bun-line bg-bun-mist text-bun-label font-semibold uppercase text-bun-faint",
        mobileCards && 'hidden lg:table-header-group',
        className
      )}
      {...props}
    >
      {children}
    </thead>
  );
}

function Body({ className, children, ...props }) {
  const mobileCards = useContext(MobileCardsContext);
  return (
    <tbody className={cn("divide-y divide-bun-line-soft", mobileCards && 'block lg:table-row-group', className)} {...props}>
      {children}
    </tbody>
  );
}

const Row = React.forwardRef(function Row({ selected = false, interactive = false, className, children, ...props }, ref) {
  const mobileCards = useContext(MobileCardsContext);
  return (
    <tr
      ref={ref}
      aria-selected={selected || undefined}
      className={cn(
        "transition-colors",
        mobileCards && 'grid grid-cols-2 gap-x-3 gap-y-2 px-[18px] py-3 lg:table-row lg:p-0',
        selected ? "bg-bun-wash" : interactive && "hover:bg-bun-mist",
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

function Header({ scope = "col", className, children, ...props }) {
  return (
    <th
      scope={scope}
      className={cn("whitespace-nowrap px-4 py-[9px] text-left", className)}
      {...props}
    >
      {children}
    </th>
  );
}

function Cell({ mobileLabel, mobilePrimary = false, className, children, ...props }) {
  const mobileCards = useContext(MobileCardsContext);
  return (
    <td className={cn(mobileCards ? 'block min-w-0 break-words text-left lg:table-cell lg:px-4 lg:py-[11px]' : "px-4 py-[11px]", 'align-middle', mobileCards && mobilePrimary && 'col-span-2', className)} {...props}>
      {mobileCards && mobileLabel && <span className="mb-1 block text-bun-label font-semibold uppercase text-bun-stone lg:hidden">{mobileLabel}</span>}
      {children}
    </td>
  );
}

function Footer({ className, children, ...props }) {
  const mobileCards = useContext(MobileCardsContext);
  return (
    <tfoot
      className={cn(
        "border-t border-bun-line bg-bun-surface text-bun-meta text-bun-stone",
        mobileCards && 'block lg:table-footer-group',
        className
      )}
      {...props}
    >
      {children}
    </tfoot>
  );
}

DataTable.Caption = Caption;
DataTable.Head = Head;
DataTable.Body = Body;
DataTable.Row = Row;
DataTable.Header = Header;
DataTable.Cell = Cell;
DataTable.Footer = Footer;

/** Read-only cell content that opens a detail view without looking like a form. */
function CellButton({ wrap = true, className, children, ...props }) {
  return <button type="button" className={cn(
    "block min-h-11 min-w-11 w-full sm:min-h-0 sm:min-w-0 cursor-pointer rounded-bun-sm text-left text-inherit normal-case hover:bg-bun-mist focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue disabled:cursor-not-allowed disabled:text-bun-stone disabled:hover:bg-transparent",
    wrap ? "max-w-[30rem] whitespace-pre-wrap [overflow-wrap:anywhere]" : "max-w-[18rem] truncate",
    className
  )} {...props}>{children}</button>;
}
DataTable.CellButton = CellButton;
